import moment from "moment";
import { resolveGradingScheme } from './gradingScheme'

/**
 * Quarter Management Utilities
 * Centralized utilities for handling quarter logic across the application
 */

// Quarter to month mapping
export const QUARTER_MONTHS = {
  1: { start: 7, end: 9 }, // July - September
  2: { start: 10, end: 12 }, // October - December
  3: { start: 1, end: 2 }, // January - February
  4: { start: 3, end: 6 }, // March - June
};

// Database field mappings for quarters
export const QUARTER_DB_FIELDS = {
  grades: {
    1: "firstQuarter",
    2: "secondQuarter",
    3: "thirdQuarter",
    4: "fourthQuarter",
  },
  conducts: {
    1: "firstQuarter",
    2: "secondQuarter",
    3: "thirdQuarter",
    4: "fourthQuarter",
  },
  conductDetails: {
    1: "firstQuarter1",
    2: "secondQuarter1",
    3: "thirdQuarter1",
    4: "fourthQuarter1",
  },
};

/**
 * Which period the school is currently in.
 *
 * The period count comes from the resolver rather than being assumed: this used
 * to map March-June to quarter 4, which under the three-term June-start
 * calendar meant the app opened on a period that does not exist - and a
 * quarter-4 write against a three-term scheme resolves to no column at all.
 *
 * @returns {number} the current period, 1..periodCount
 */
// DO 009, s. 2026: the school year opens in June.
const SCHOOL_YEAR_START_MONTH = 6;

export const getCurrentQuarter = () => {
  const periodCount = resolveGradingScheme(getCurrentSchoolYear(), null).periods.length;
  const currentMonth = moment().month() + 1;

  // Legacy four-quarter years opened in July and were not evenly split, so this
  // mapping is kept exactly as it was rather than derived.
  if (periodCount === 4) {
    if (currentMonth >= 7 && currentMonth <= 9) return 1;
    if (currentMonth >= 10 && currentMonth <= 12) return 2;
    if (currentMonth >= 1 && currentMonth <= 2) return 3;
    if (currentMonth >= 3 && currentMonth <= 6) return 4;
    return 1;
  }

  // Three terms across a June-to-April year (DO 009), written out month by
  // month rather than divided evenly.
  //
  // An even split put October in Term 2, but the school was still finishing
  // Term 1 then - so the app opened on a term nobody was working on, the
  // dashboard looked empty, and 43 club marks were typed into Term 2 by
  // moderators who never noticed the selector had moved. The boundaries are
  // the school's, not the calendar's, so they are stated plainly here and are
  // the one place to change when the school year shifts.
  const THREE_TERM_MONTHS = {
    6: 1, 7: 1, 8: 1, 9: 1, 10: 1,   // June to October
    11: 2, 12: 2, 1: 2,              // November to January
    2: 3, 3: 3, 4: 3, 5: 3,          // February to April, and the tail
  };

  return THREE_TERM_MONTHS[currentMonth] || 1;
};

/**
 * Get the quarter label for display
 * @param {number} quarterNumber - Quarter number (1-4)
 * @returns {string} Quarter label
 */
export const getQuarterLabel = (quarterNumber) => {
  const labels = {
    1: "1st Quarter",
    2: "2nd Quarter",
    3: "3rd Quarter",
    4: "4th Quarter",
  };
  return labels[quarterNumber] || "Unknown Quarter";
};

/**
 * Get the date range for a specific quarter
 * @param {number} quarter - Quarter number (1-4)
 * @param {number} schoolYear - School year (e.g., 2024)
 * @returns {Object} Object with start and end dates
 */
export const getQuarterDateRange = (quarter, schoolYear) => {
  const quarterMonths = QUARTER_MONTHS[quarter];
  if (!quarterMonths) {
    throw new Error(`Invalid quarter: ${quarter}`);
  }

  let startYear = schoolYear;
  let endYear = schoolYear;
  
  // For quarters that span across years (Q3 and Q4 are in the next calendar year)
  if (quarter === 3 || quarter === 4) {
    startYear = schoolYear + 1;
    endYear = schoolYear + 1;
  }

  const start = moment()
    .year(startYear)
    .month(quarterMonths.start - 1)
    .startOf("month");

  const end = moment()
    .year(endYear)
    .month(quarterMonths.end - 1)
    .endOf("month");

  return {
    start: start.toDate(),
    end: end.toDate(),
    startFormatted: start.format("MMMM YYYY"),
    endFormatted: end.format("MMMM YYYY"),
  };
};

/**
 * Get the database field name for a specific quarter and data type
 * @param {number} quarter - Quarter number (1-4)
 * @param {string} dataType - Data type ('grades', 'conducts', 'conductDetails')
 * @param {number} detailIndex - Detail index for conduct details (1-5)
 * @returns {string} Database field name
 */
export const getQuarterDbField = (quarter, dataType = "grades", detailIndex = null) => {
  const fields = QUARTER_DB_FIELDS[dataType];
  if (!fields) {
    throw new Error(`Invalid data type: ${dataType}`);
  }

  const field = fields[quarter];
  if (!field) {
    throw new Error(`Invalid quarter: ${quarter}`);
  }

  // For conduct details, append the detail index
  if (dataType === "conductDetails" && detailIndex) {
    return `${field}${detailIndex}`;
  }

  return field;
};

/**
 * Check if a quarter is valid
 * @param {number} quarter - Quarter number to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidQuarter = (quarter) => {
  return quarter >= 1 && quarter <= 4;
};

/**
 * Get all quarters as an array of objects for dropdowns
 * @returns {Array} Array of quarter objects
 */
export const getQuarterOptions = () => {
  return [
    { key: "1", text: "1st Quarter", value: 1 },
    { key: "2", text: "2nd Quarter", value: 2 },
    { key: "3", text: "3rd Quarter", value: 3 },
    { key: "4", text: "4th Quarter", value: 4 },
  ];
};

/**
 * Get the next quarter
 * @param {number} currentQuarter - Current quarter (1-4)
 * @returns {number} Next quarter (1-4)
 */
export const getNextQuarter = (currentQuarter) => {
  if (!isValidQuarter(currentQuarter)) {
    throw new Error(`Invalid quarter: ${currentQuarter}`);
  }
  return currentQuarter === 4 ? 1 : currentQuarter + 1;
};

/**
 * Get the previous quarter
 * @param {number} currentQuarter - Current quarter (1-4)
 * @returns {number} Previous quarter (1-4)
 */
export const getPreviousQuarter = (currentQuarter) => {
  if (!isValidQuarter(currentQuarter)) {
    throw new Error(`Invalid quarter: ${currentQuarter}`);
  }
  return currentQuarter === 1 ? 4 : currentQuarter - 1;
};

/**
 * Check if a quarter is complete based on current date
 * @param {number} quarter - Quarter to check (1-4)
 * @param {number} schoolYear - School year
 * @returns {boolean} True if quarter is complete
 */
export const isQuarterComplete = (quarter, schoolYear) => {
  const quarterRange = getQuarterDateRange(quarter, schoolYear);
  return moment().isAfter(quarterRange.end);
};

/**
 * Get current school year based on current date
 * @returns {number} Current school year
 */
export const getCurrentSchoolYear = () => {
  const currentDate = moment();
  const currentMonth = currentDate.month() + 1;
  const currentYear = currentDate.year();
  
  // School year starts in June (DO 009, s. 2026 - SY 2026-2027 opens 8 June 2026).
  // attendance-format-sy.json has recorded a June start since SY 2025-2026.
  return currentMonth >= SCHOOL_YEAR_START_MONTH ? currentYear : currentYear - 1;
};

/**
 * Get enrollment school year (the upcoming school year for enrollment purposes).
 * Enrollment targets the school year starting in July of the current calendar year.
 * @returns {number} Start year of the enrollment school year
 */
export const getEnrollmentSchoolYear = () => moment().year();

/**
 * Format a school year start year into a display string.
 * @param {number} startYear - The start year of the school year
 * @returns {string} Formatted school year string (e.g., "SY 2026-2027")
 */
export const formatSchoolYear = (startYear) => `SY ${startYear}-${startYear + 1}`;